import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredComics, categories } from '@/mocks/comics';

type SortType = 'newest' | 'ranking' | 'popular';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('type') || '全部');
  const [sortType, setSortType] = useState<SortType>((searchParams.get('sort') as SortType) || 'ranking');
  const [currentPage, setCurrentPage] = useState(1);
  const comicsPerPage = 12;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredComics = useMemo(() => {
    let result = [...featuredComics];
    if (activeCategory !== '全部') {
      result = result.filter((c) => c.category === activeCategory);
    }
    switch (sortType) {
      case 'newest':
        result.sort((a, b) => {
          if (a.updateTime.includes('小时') && b.updateTime.includes('天')) return -1;
          if (a.updateTime.includes('天') && b.updateTime.includes('小时')) return 1;
          const aNum = parseInt(a.updateTime) || 0;
          const bNum = parseInt(b.updateTime) || 0;
          if (a.updateTime.includes('小时') && b.updateTime.includes('小时')) return aNum - bNum;
          if (a.updateTime.includes('天') && b.updateTime.includes('天')) return aNum - bNum;
          return 0;
        });
        break;
      case 'ranking':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        result.sort((a, b) => b.chapters - a.chapters);
        break;
    }
    return result;
  }, [activeCategory, sortType]);

  const totalPages = Math.ceil(filteredComics.length / comicsPerPage);
  const pagedComics = filteredComics.slice(
    (currentPage - 1) * comicsPerPage,
    currentPage * comicsPerPage,
  );

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (cat !== '全部') params.set('type', cat);
    if (sortType !== 'ranking') params.set('sort', sortType);
    setSearchParams(params);
  };

  const handleSortChange = (sort: SortType) => {
    setSortType(sort);
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (activeCategory !== '全部') params.set('type', activeCategory);
    if (sort !== 'ranking') params.set('sort', sort);
    setSearchParams(params);
  };

  const pageTitle = activeCategory === '全部' ? '漫画分类大全' : `${activeCategory}漫画`;
  const pageDesc = activeCategory === '全部'
    ? '尾巴漫画网为您提供全部韩国漫画分类浏览，支持按评分、更新时间、热度排序，海量韩漫在线免费阅读。'
    : `尾巴漫画网${activeCategory}漫画分类，汇集优质${activeCategory}韩漫资源，高清无删减内容在线免费阅读，每日更新。`;

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb & Title */}
        <section className="bg-background-950 pt-24 pb-10 md:pt-32 md:pb-14">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
              <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-primary-300 whitespace-nowrap">分类大全</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">
              {pageTitle}
            </h1>
            <p className="text-foreground-400 text-sm mt-2 max-w-xl">
              {pageDesc}
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="sticky top-16 md:top-20 z-40 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto flex-wrap">
              <button
                onClick={() => handleCategoryChange('全部')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === '全部'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                全部 ({featuredComics.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.name
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sort Bar + Results */}
        <section className="bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-6">
            {/* Sort */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-foreground-500 text-sm">
                共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部漫画
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {([
                  { key: 'ranking', label: '评分最高' },
                  { key: 'newest', label: '最新更新' },
                  { key: 'popular', label: '最多章节' },
                ] as { key: SortType; label: string }[]).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => handleSortChange(s.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      sortType === s.key
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comics Grid */}
            {pagedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
                {pagedComics.map((comic) => (
                  <article key={comic.id} className="group cursor-pointer">
                    <Link to={`/comic/${comic.id}`} className="block">
                      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4 bg-background-100">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          title={`${comic.title} - 尾巴漫画网${comic.category}韩漫`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-2.5 py-1 bg-primary-500/90 backdrop-blur-sm text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                            {comic.rating}
                          </span>
                        </div>
                        {comic.status === '已完结' && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-block px-2.5 py-1 bg-background-950/70 backdrop-blur-sm text-background-50 text-xs rounded-full whitespace-nowrap">
                              已完结
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <span className="text-background-50/80 text-xs">{comic.chapters}话</span>
                          <span className="text-background-50/80 text-xs">{comic.updateTime}</span>
                        </div>
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 truncate">
                        {comic.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-foreground-500 text-xs">{comic.author}</span>
                        <span className="text-foreground-300 text-xs">·</span>
                        <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                          {comic.category}
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-emotion-sad-line text-4xl"></i>
                </span>
                <p className="text-foreground-500 text-sm">暂无该分类的漫画，请尝试其他分类</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-900 disabled:text-foreground-300 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-left-s-line"></i>
                  </span>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                      page === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-600 hover:text-foreground-900 disabled:text-foreground-300 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDesc,
            url: `http://sansevieria.cn/category${activeCategory !== '全部' ? `?type=${activeCategory}` : ''}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '尾巴漫画网',
              url: 'http://sansevieria.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://sansevieria.cn/' },
                { '@type': 'ListItem', position: 2, name: pageTitle },
              ],
            },
          }),
        }}
      />
    </>
  );
}